import _jsx from "@babel/runtime/helpers/jsx";
import React, { memo, useState, useEffect, createContext, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import hasDom from 'has-dom';
import _throttle from 'lodash/throttle';

/**
 *  The Sticky Container houses the context that is used between the StickyElement and Sticky Trigger.
 *  It passes down scroll direction and isSticky object. It also handles the toggling of the keys within the
 *  isSticky object.
 **/

export const StickyContainerContext = /*#__PURE__*/createContext();
const events = ['resize', 'scroll', 'touchstart', 'touchmove', 'touchend', 'pageshow', 'load'];
let lastScrollPos = 0;
const StickyContainer = /*#__PURE__*/memo(props => {
  const [isScrollingUp, setIsScrollingUp] = useState(false);
  const [isSticky, setIsSticky] = useState({});
  const isUnMounted = useRef(false);

  // eslint-disable-next-line no-unused-vars
  const [onLoadHandled, setOnLoadHandled] = useState(false);
  const handleScroll = event => {
    let {
      scrollingElement
    } = event.target;
    // Polyfill for scrollingElement
    if (hasDom() && !scrollingElement) {
      scrollingElement = event.currentTarget || document.documentElement || document.body;
    }
    const {
      scrollTop
    } = scrollingElement;
    if (!isUnMounted.current) {
      if (lastScrollPos > scrollTop) {
        setIsScrollingUp(true);
      } else if (lastScrollPos < scrollTop) {
        setIsScrollingUp(false);
      }
      lastScrollPos = scrollTop;
    }
  };
  useEffect(() => {
    isUnMounted.current = false;
    if (props.addEventListeners && window) {
      events.forEach(event => window.addEventListener(event, _throttle(handleScroll, 350, {
        leading: true
      }), false));
    }
    return () => {
      if (props.addEventListeners && window) {
        events.forEach(event => window.removeEventListener(event, _throttle(handleScroll, 350, {
          leading: true
        }), false));
      }
      isUnMounted.current = true;
    };
  }, [props.addEventListeners]);
  const toggleSticky = useCallback(changedStickyValues => {
    if (!isUnMounted.current) {
      const newIsStickyState = {
        ...isSticky,
        ...changedStickyValues.isSticky
      };
      setIsSticky(newIsStickyState);
    }
  }, [isSticky]);
  const providerValue = useMemo(() => ({
    isSticky,
    isScrollingUp,
    toggleSticky,
    onLoadHandled
  }), [isSticky, isScrollingUp, toggleSticky, onLoadHandled]);
  return /*#__PURE__*/_jsx("div", {
    "data-cmp": "stickyContainer",
    onScroll: handleScroll,
    onTouchStart: handleScroll,
    onTouchMove: handleScroll,
    onTouchEnd: handleScroll
  }, void 0, /*#__PURE__*/_jsx(StickyContainerContext.Provider, {
    value: providerValue
  }, void 0, props.children));
});
export default StickyContainer;